import React, { memo, SyntheticEvent, useState } from "react";

import { Loader as DefaultLoader } from "../loader";

import styles from "./styles.modules.scss";
import { GameName, gameSettings } from "inbox-contentful";

const addFormatToSources = (
    format: string,
    sources: ImageSourceSets,
): ImageSourceSets => {
    return Object.fromEntries(
        Object.entries(sources).map(([key, value]) => {
            const { path } = value;

            // Contentful resizing of SVGs is really blurry and crappy, so we'll skip over them
            if (path?.includes(".svg")) {
                return [key, value];
            }

            // Skip AVIF for GIFs (iOS Safari has transparency issues with AVIF)
            if (format === "avif" && path?.includes(".gif")) {
                return [key, value];
            }

            const withFm = path?.includes("?")
                ? `${path}&fm=${format}`
                : `${path}?fm=${format}`;

            return [key, { ...value, path: withFm }];
        }),
    ) as ImageSourceSets;
};

const SourceSet = ({
    referenceWidth,
    sources,
    type,
}: {
    referenceWidth: number;
    sources: ImageSourceSets;
    type?: string;
}) => {
    const { small, smallRetina, medium, mediumRetina, large, largeRetina } =
        sources;

    const largeMediaQuery = `(min-width: ${Math.round(
        referenceWidth * 0.8,
    )}px)`;
    const mediumMediaQuery = `(min-width: ${Math.round(
        referenceWidth * 0.4,
    )}px)`;

    return (
        <>
            <source
                srcSet={`${large.path} 1x, ${largeRetina.path} 2x`}
                media={largeMediaQuery}
                type={type}
            />

            <source
                srcSet={`${medium.path} 1x, ${mediumRetina.path} 2x`}
                media={mediumMediaQuery}
                type={type}
            />

            <source
                srcSet={`${small.path} 1x, ${smallRetina.path} 2x`}
                type={type}
            />
        </>
    );
};

export const Image = memo(
    ({
        sources,
        onLoad,
        alt,
        cover,
        Loader,
        extraStyles,
        fullResolution,
    }: {
        sources: ImageSourceSets;
        cover?: boolean;
        alt?: string;
        Loader?: JSX.Element | null;
        extraStyles?: React.CSSProperties;
        fullResolution?: boolean;
        onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
    }) => {
        if (!sources) {
            return null;
        }

        const [loadedSrc, setLoadedSrc] = useState("");
        const referenceWidth =
            gameSettings[window.GAMENAME as GameName].referenceWidth;

        if (fullResolution) {
            sources = Object.fromEntries(
                Object.entries(sources).map(([key, value]) => {
                    const { path } = value;

                    const paramsString = path.split("?")[1];
                    if (!paramsString) {
                        return [key, value];
                    }

                    const searchParams = new URLSearchParams(paramsString);
                    searchParams.delete("w");
                    searchParams.delete("h");

                    const newPath =
                        path.split("?")[0] + "?" + searchParams.toString();

                    return [key, { ...value, path: newPath }];
                }),
            ) as ImageSourceSets;
        }

        const sourcesWithAvif = addFormatToSources("avif", sources);
        const sourcesWithWebp = addFormatToSources("webp", sources);
        const sourcesWithPng = addFormatToSources("png", sources);
        const { small } = sources;

        const background = cover ? loadedSrc : undefined;
        const loaded = loadedSrc.length > 0;

        const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
            setLoadedSrc(event.currentTarget.currentSrc);
            if (onLoad) {
                onLoad(event);
            }
        };

        return (
            <div
                className={`image ${styles.image} ${
                    loaded ? `is-loaded ${styles.isLoaded}` : ""
                } ${cover ? `cover ${styles.cover}` : ""}`}
            >
                <div className={`container ${styles.container}`}>
                    {Loader === null ||
                        (!loaded && (
                            <div
                                className={`loader-container ${styles.loaderContainer}`}
                            >
                                {Loader || <DefaultLoader />}
                            </div>
                        ))}

                    <picture
                        className={`picture ${styles.picture}`}
                        {...(background && {
                            style: {
                                backgroundImage: `url(${background})`,
                                ...(extraStyles ? extraStyles : {}),
                            },
                        })}
                    >
                        <SourceSet
                            sources={sourcesWithAvif}
                            referenceWidth={referenceWidth}
                            type="image/avif"
                        />

                        <SourceSet
                            sources={sourcesWithWebp}
                            referenceWidth={referenceWidth}
                            type="image/webp"
                        />

                        <SourceSet
                            sources={sourcesWithPng}
                            referenceWidth={referenceWidth}
                            type="image/png"
                        />

                        <img
                            alt={alt}
                            className={`img ${styles.img}`}
                            draggable={false}
                            height={small.height}
                            onLoad={handleLoad}
                            src={small.path}
                            width={small.width}
                        />
                    </picture>
                </div>
            </div>
        );
    },
    (current, next) => current.sources === next.sources,
);
